<template>
  <div class="container-fluid">
    <h1 class="mb-3">Customer Management</h1>
    
    <div v-if="auth.isAdmin" class="row mb-3 align-items-center">
      <AddActions view="Customer" @new="newModal"/>
      <div class="col d-flex justify-content-end">
        <ToggleLockedButton 
          v-model="includeLocked" 
          class="me-2"
          @toggle="toggleLockedFilter"
        />
      </div>

    </div>
    <div>
      <CustomerForm v-if="modalNew || modalEdit || modalDetail"
        :msg="`${modalMsg} Customer`" :tag="`customer`"
        :schema="entitySchema" :schemaTypes="schemaTypes" :parentObj="editCustomer"
        @formDataSubmitted="handleSave" @formClosed="closedModal"
      />
    </div>
    <CustomerTable 
      modelName="Customer" 
      :columns="columns" 
      :ajaxUrl="customerUrl"
      :queryParams="customerQueryParams" 
      :options="tableOptions" 
      ref="table"
    >
      <template #details="row">
        <TableActions mode="details" :rowData="row.rowData" @detail="detail" />
      </template>
      <template #actions="row">
        <TableActions mode="actions" :rowData="row.rowData" @edit="edit" @remove="remove" />
      </template>
    </CustomerTable>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useSchemaStore } from '@/stores/schemaStore';
import { ApiEndpoint } from '@/utils/config';
import { getCommonColumns } from '@/utils/table';
import { handleRowDelete, handleRowSave } from '@/utils/form';

import CustomerTable from '@/components/TableComponent.vue';
import TableActions from '@/components/TableActions.vue';
import AddActions from '@/components/AddActions.vue';
import CustomerForm from '@/components/DataFormModal.vue';
import ToggleLockedButton from '@/components/ToggleLockedButton.vue';

const auth = useAuthStore();
const schema = useSchemaStore();

const table = ref();
const customerList = ref([]);
const entitySchema = ref([]);
const schemaTypes = reactive({});

const includeLocked = ref(false);

const modalNew = ref(false);
const modalEdit = ref(false);
const modalDetail = ref(false);
const editCustomer = reactive({});

const modalMsg = computed(() => {
  if (modalNew.value) return 'Add';
  if (modalEdit.value) return 'Edit';
  return 'View';
});

const columns = [
  { title: 'Name', data: 'name' },
  { title: 'Customer ID', data: 'id' },
  { title: 'Parent ID', data: 'parentId', visible: auth.isAdmin },
  {
    title: 'Provisioning ID',
    data: 'provisioningId',
    visible: auth.isAdmin,
    defaultContent: '-',
  },
  { 
    title: 'Provisioning API Key',
    data: 'provisioningApiKey',
    defaultContent: '',
    visible: false,
    className: 'td-truncate',
  },
  ...getCommonColumns(auth.isAdmin),
];

const tableOptions = computed(() => {
  // return getTableOptions(auth.isAdmin, 'Customer', columns);
});

const customerUrl = ApiEndpoint.CUSTOMERS;
const customerQueryParams = computed(() => {
  const params = {
    includeLocked: includeLocked.value ? includeLocked.value : undefined,
  };
  return params;
});

const loadCustomers = async (forceRefresh = false) => {
  await nextTick();
  // Force DataTables to read parent boundaries and execute its first clean server network fetch
  if (table.value) {
    await table.value.reloadTable(forceRefresh);
  }
};

const toggleLockedFilter = async () => {
  await loadCustomers(true);
};

const resetCustomer = (obj) => {
  Object.keys(obj).forEach(key => delete obj[key]);
};

const newModal = () => {
  resetCustomer(editCustomer);
  modalNew.value = true;
};

const handleSave = async (detail) => {
  try {
    await handleRowSave('customer', detail, { ...editCustomer }, customerList);
    closedModal();
    await loadCustomers(true);
  } catch (err) {
    console.error(err);
  }
};

const edit = (row) => {
  Object.assign(editCustomer, row);
  modalEdit.value = true;
};

const detail = (row) => {
  Object.assign(editCustomer, row);
  modalDetail.value = true;
};

const remove = async (row) => {
  const validationKeys = ['name'];
  await handleRowDelete('customer', row, validationKeys);
  await loadCustomers(true);
}

/** Resets modal values after modal close, ready for next modal */
const closedModal = () => {
  modalNew.value = modalEdit.value = modalDetail.value = false;
};

onMounted(async () => {
  entitySchema.value = schema.getFieldMeta('customer');
  schemaTypes.value = schema.getModelEnums('customer');
});
</script>

<style scoped>
.pointer { cursor: pointer; }
.action { height: 20px; width: auto; }
</style>
